/**
 * Spec 052, step 6: one client's order. On top — who it is for, its stage with the moves the
 * funnel allows, the manager, who answers for it and the money (by the right); under it the
 * blocks: milestones (what the client will see), documents, the units' work, the code and the
 * history. «Как видит клиент» shows exactly the client's view. Approving a document, marking a
 * payment and downloading the source are a person's — here, in the app. Spec 054, step 3:
 * «Переписка» comes first — the client's chat, which a person answers here; a document goes to
 * the client from «Документы». Spec 061: a document goes as a Word file; the client's signed
 * contract — one of its files in the chat — is marked here («Подписанный договор»), and the order
 * goes «в работе».
 */
import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Download, Eye, KeyRound, Plus } from "lucide-react";

import { VersionConflictError } from "@/api/client";
import {
  ClientChat,
  OrderCode,
  Orders,
  type DocumentOut,
  type MilestoneOut,
  type OrderCodeReview,
  type OrderOut,
} from "@/api/orders";
import { Agents, Overview } from "@/api/resources";
import { AccessLinkSheet } from "@/components/ClientAccesses";
import { chatKey, ClientChatTab } from "@/components/ClientChatTab";
import { DocumentFiles } from "@/components/ClientDocuments";
import { ChatSummaryBlock } from "@/components/ClientMemory";
import { OrderCodeReviewPanel, REVIEW_LABEL, useOrderCodeRefresh } from "@/components/OrderCodeReview";
import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { DateField, SelectField, TextArea, TextField } from "@/components/ui/Field";
import { Sheet } from "@/components/ui/Sheet";
import { SkeletonList } from "@/components/ui/Skeleton";
import { Mark } from "@/components/ui/Status";
import { useToast } from "@/components/ui/Toast";
import { ago, AppBar, BlockTabs, blockPanel, EmptyNote, ScreenHeader, Switch, whoDid } from "@/kit";
import { localDay } from "@/lib/decide";
import { Markdown } from "@/lib/markdown";
import {
  day,
  DOCUMENT_KINDS,
  DOCUMENT_LABEL,
  DOCUMENT_STATE,
  documentName,
  eventLine,
  FINISHED,
  movesFrom,
  parseRub,
  rub,
  STAGE_LABEL,
  type DocumentKind,
  type Stage,
} from "@/lib/orders";
import { useBackButton } from "@/lib/telegram-ui";
import { LostSheet, ORDERS_KEY, stageLine } from "@/screens/OrdersScreen";

type Block = "chat" | "milestones" | "documents" | "work" | "code" | "history";

export function OrderScreenRoute() {
  useBackButton("/projects?tab=orders");
  const { order_id } = useParams<{ order_id: string }>();
  return order_id ? <OrderScreen orderId={order_id} /> : null;
}

/** The order's queries refresh together after any change. */
function useOrderRefresh(orderId: string) {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: ["client-order", orderId] });
    void qc.invalidateQueries({ queryKey: ORDERS_KEY });
  };
}

function useFailure(orderId: string) {
  const toast = useToast();
  const refresh = useOrderRefresh(orderId);
  return (error: Error) => {
    toast.error(error instanceof VersionConflictError ? "Заказ изменился, пока ты смотрел. Обновили — проверь и повтори." : error.message);
    refresh();
  };
}

export function OrderScreen({ orderId }: { orderId: string }) {
  const [block, setBlock] = useState<Block>("chat");
  const order = useQuery({ queryKey: ["client-order", orderId], queryFn: () => Orders.get(orderId), refetchInterval: 30_000 });
  const o = order.data;
  const drafts = o?.documents.filter((d) => d.state === "draft").length ?? 0;
  return (
    <div className="flex max-w-[860px] flex-col gap-3">
      <AppBar fallback="/projects?tab=orders" context="Заказы" />
      {order.isLoading && <SkeletonList count={3} />}
      {order.isError && <EmptyNote action={<Button size="compact" variant="secondary" onClick={() => void order.refetch()}>Повторить</Button>}>Заказ не загрузился.</EmptyNote>}
      {o && (
        <>
          <ScreenHeader title={o.title} sub={<>{o.label} · <Link to={`/clients/${o.client.id}`} className="text-p-link">{o.client.name}</Link> · {o.area.name}</>} />
          <OrderHead order={o} />
          <BlockTabs<Block>
            id="order"
            label="Разделы заказа"
            value={block}
            onChange={setBlock}
            tabs={[
              { key: "chat", label: "Переписка" },
              { key: "milestones", label: "Вехи", count: o.milestones.length },
              { key: "documents", label: "Документы", count: o.documents.length, alert: drafts > 0 },
              { key: "work", label: "Поручения", count: o.unit_orders.length },
              { key: "code", label: "Код" },
              { key: "history", label: "История" },
            ]}
          />
          <div {...blockPanel("order", block)} className="flex flex-col gap-3 outline-none">
            {block === "chat" && (
              <>
                <ChatSummaryBlock orderId={o.id} />
                <ClientChatTab order={o} />
              </>
            )}
            {block === "milestones" && <Milestones order={o} />}
            {block === "documents" && <Documents order={o} />}
            {block === "work" && <UnitWork order={o} />}
            {block === "code" && <CodeBlock order={o} />}
            {block === "history" && <History order={o} />}
          </div>
        </>
      )}
    </div>
  );
}

/* ── The head ───────────────────────────────────────────────────────────────────────── */

function OrderHead({ order: o }: { order: OrderOut }) {
  const refresh = useOrderRefresh(o.id);
  const failed = useFailure(o.id);
  const toast = useToast();
  const [target, setTarget] = useState("");
  const [losing, setLosing] = useState(false);
  const [handing, setHanding] = useState(false);
  const [editing, setEditing] = useState(false);
  const [paying, setPaying] = useState(false);
  const [client, setClient] = useState(false);
  const [accesses, setAccesses] = useState(false);
  const moves = movesFrom(o.stage);
  const move = useMutation({
    mutationFn: ({ stage, reason }: { stage: Stage; reason?: string }) => Orders.move(o.id, { version: o.version, stage, reason_md: reason ?? null }),
    onSuccess: (next) => { toast.success(`Этап: ${STAGE_LABEL[next.stage]}`); setTarget(""); setLosing(false); refresh(); },
    onError: failed,
  });
  const go = () => {
    if (!target) return;
    if (target === "lost") setLosing(true);
    else move.mutate({ stage: target as Stage });
  };
  return (
    <Card className="flex flex-col gap-3">
      <dl className="grid gap-x-6 gap-y-2 text-body sm:grid-cols-2">
        <div><dt className="text-meta text-p-text-muted">Этап</dt><dd className="font-semibold text-p-text">{stageLine(o)}</dd></div>
        <div><dt className="text-meta text-p-text-muted">Менеджер</dt><dd className="text-p-text">{o.manager?.name ?? "пока никто"}</dd></div>
        <div><dt className="text-meta text-p-text-muted">Отвечает</dt><dd className="text-p-text">{o.owner.name}</dd></div>
        {(o.amount_rub != null || o.paid_rub != null) && (
          <div>
            <dt className="text-meta text-p-text-muted">Сумма</dt>
            <dd className="text-p-text">{o.amount_rub != null ? rub(o.amount_rub) : "КП ещё не утверждено"}{o.paid_rub ? ` · оплачено ${rub(o.paid_rub)}` : ""}</dd>
          </div>
        )}
      </dl>
      {moves.length > 0 && (
        <div className="flex flex-wrap items-end gap-2">
          <div className="min-w-[200px] flex-1">
            <SelectField label="Перевести на этап" value={target} onChange={setTarget} placeholder="Выбери этап"
              options={moves.map((s) => ({ value: s, label: STAGE_LABEL[s] }))} />
          </div>
          <Button disabled={!target} loading={move.isPending} loadingText="Переводим…" onClick={go}>Перевести</Button>
        </div>
      )}
      {o.stage === "contract" && <p className="text-meta text-p-text-muted">На «Договоре» открывается код заказа — он во вкладке «Код».</p>}
      <div className="flex flex-wrap gap-2">
        <Button size="compact" variant="secondary" onClick={() => setHanding(true)}>Передать менеджеру</Button>
        <Button size="compact" variant="secondary" onClick={() => setEditing(true)}>Изменить</Button>
        {o.paid_rub != null && <Button size="compact" variant="secondary" onClick={() => setPaying(true)}>Отметить оплату</Button>}
        <Button size="compact" variant="ghost" icon={<Eye className="h-4 w-4" aria-hidden="true" />} onClick={() => setClient(true)}>Как видит клиент</Button>
        {o.stage !== "closed" && o.stage !== "lost" && (
          <Button size="compact" variant="ghost" icon={<KeyRound className="h-4 w-4" aria-hidden="true" />} onClick={() => setAccesses(true)}>Ссылка для доступов</Button>
        )}
      </div>
      {o.brief_md && (
        <details className="space-y-2">
          <summary className="min-h-11 cursor-pointer py-2 text-body font-semibold">Что хочет клиент</summary>
          <Markdown text={o.brief_md} className="text-body text-p-text" />
        </details>
      )}
      <LostSheet key={losing ? "open" : "shut"} order={losing ? o : null} busy={move.isPending} onClose={() => setLosing(false)}
        onConfirm={(reason) => move.mutate({ stage: "lost", reason })} />
      <HandOverSheet key={`hand-${o.version}`} order={o} open={handing} onClose={() => setHanding(false)} />
      <EditSheet key={`edit-${o.version}`} order={o} open={editing} onClose={() => setEditing(false)} />
      <PaymentSheet key={`pay-${o.version}`} order={o} open={paying} onClose={() => setPaying(false)} />
      <ClientViewSheet order={o} open={client} onClose={() => setClient(false)} />
      <AccessLinkSheet key={accesses ? "open" : "shut"} orderId={o.id} label={o.label} open={accesses} onClose={() => setAccesses(false)} />
    </Card>
  );
}

function HandOverSheet({ order: o, open, onClose }: { order: OrderOut; open: boolean; onClose: () => void }) {
  const refresh = useOrderRefresh(o.id);
  const failed = useFailure(o.id);
  const [manager, setManager] = useState(o.manager?.id ?? "");
  const [note, setNote] = useState("");
  const staff = useQuery({ queryKey: ["agents", "staff"], queryFn: () => Agents.list(), enabled: open, select: (a) => a.filter((x) => !x.archived_at && !x.parent_id) });
  const save = useMutation({
    mutationFn: () => Orders.handOver(o.id, { version: o.version, manager_agent_id: manager || null, note_md: note.trim() || null }),
    onSuccess: () => { refresh(); onClose(); },
    onError: failed,
  });
  return (
    <Sheet open={open} onClose={onClose} title="Кто ведёт заказ"
      footer={<Button block disabled={manager === (o.manager?.id ?? "")} loading={save.isPending} loadingText="Передаём…" onClick={() => save.mutate()}>Передать</Button>}>
      <div className="flex flex-col gap-3">
        <SelectField label="Менеджер" value={manager} onChange={setManager} placeholder="Пока никто"
          options={(staff.data ?? []).map((a) => ({ value: a.id, label: a.name }))} />
        <TextArea label="Зачем (в историю)" value={note} onChange={(e) => setNote(e.target.value)} maxLength={2000} hint="После договора заказ обычно ведёт менеджер клиента." />
      </div>
    </Sheet>
  );
}

function EditSheet({ order: o, open, onClose }: { order: OrderOut; open: boolean; onClose: () => void }) {
  const refresh = useOrderRefresh(o.id);
  const failed = useFailure(o.id);
  const [title, setTitle] = useState(o.title);
  const [brief, setBrief] = useState(o.brief_md ?? "");
  const save = useMutation({
    mutationFn: () => Orders.update(o.id, { version: o.version, title: title.trim(), brief_md: brief.trim() || null }),
    onSuccess: () => { refresh(); onClose(); },
    onError: failed,
  });
  return (
    <Sheet open={open} onClose={onClose} title={o.label}
      footer={<Button block disabled={!title.trim()} loading={save.isPending} loadingText="Сохраняем…" onClick={() => save.mutate()}>Сохранить</Button>}>
      <div className="flex flex-col gap-3">
        <TextField label="Что делаем" value={title} onChange={(e) => setTitle(e.target.value)} maxLength={200} />
        <TextArea label="Что хочет клиент" value={brief} onChange={(e) => setBrief(e.target.value)} maxLength={20000}
          hint="Люди клиента и их контакты — в паспорте клиента." />
      </div>
    </Sheet>
  );
}

function PaymentSheet({ order: o, open, onClose }: { order: OrderOut; open: boolean; onClose: () => void }) {
  const refresh = useOrderRefresh(o.id);
  const failed = useFailure(o.id);
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const value = parseRub(amount);
  const error = amount.trim() && (value == null || value === 0) ? "Сумма в рублях, не ноль" : undefined;
  const needsNote = value != null && value < 0 && !note.trim();
  const save = useMutation({
    mutationFn: () => Orders.payment(o.id, { version: o.version, amount_rub: value!, note_md: note.trim() || null }),
    onSuccess: () => { refresh(); onClose(); },
    onError: failed,
  });
  return (
    <Sheet open={open} onClose={onClose} title="Оплата"
      footer={<Button block disabled={value == null || value === 0 || needsNote} loading={save.isPending} loadingText="Отмечаем…" onClick={() => save.mutate()}>Отметить</Button>}>
      <div className="flex flex-col gap-3">
        <TextField label="Пришло, ₽" value={amount} onChange={(e) => setAmount(e.target.value)} inputMode="numeric" error={error}
          hint="Целые рубли. Исправить ошибку — та же сумма с минусом и пояснением." />
        <TextArea label="Пояснение" value={note} onChange={(e) => setNote(e.target.value)} maxLength={2000} error={needsNote ? "Исправление — с пояснением" : undefined} />
        <p className="text-meta text-p-text-muted">Оплачено сейчас: {rub(o.paid_rub ?? 0)}.</p>
      </div>
    </Sheet>
  );
}

function ClientViewSheet({ order: o, open, onClose }: { order: OrderOut; open: boolean; onClose: () => void }) {
  const view = useQuery({ queryKey: ["client-order", o.id, "public"], queryFn: () => Orders.public(o.id), enabled: open });
  return (
    <Sheet open={open} onClose={onClose} title="Как видит клиент">
      {view.isLoading && <SkeletonList count={2} />}
      {view.isError && <EmptyNote>Не удалось загрузить.</EmptyNote>}
      {view.data && (
        <div className="flex flex-col gap-3">
          <p className="text-meta text-p-text-muted">Только это — без денег, менеджера и внутренних дел.</p>
          <p className="text-cell-title font-semibold text-p-text">{view.data.label} · {view.data.title}</p>
          <p className="text-body text-p-text">{view.data.stage}</p>
          <ol className="flex flex-col gap-2">
            {view.data.milestones.map((m, i) => (
              <li key={i} className="rounded-xl border border-p-border px-3 py-2">
                <p className="font-semibold text-p-text">{m.title}</p>
                <p className="text-meta text-p-text-muted">{m.done_at ? `готово ${day(m.done_at)}` : m.planned_at ? `к ${day(m.planned_at)}` : "без срока"}{m.status_note ? ` · ${m.status_note}` : ""}</p>
              </li>
            ))}
            {view.data.milestones.length === 0 && <li className="text-meta text-p-text-muted">Вех для клиента пока нет.</li>}
          </ol>
        </div>
      )}
    </Sheet>
  );
}

/* ── Milestones ─────────────────────────────────────────────────────────────────────── */

function Milestones({ order: o }: { order: OrderOut }) {
  const refresh = useOrderRefresh(o.id);
  const failed = useFailure(o.id);
  const [editing, setEditing] = useState<MilestoneOut | "new" | null>(null);
  const done = useMutation({
    mutationFn: (m: MilestoneOut) => Orders.updateMilestone(o.id, m.id, { version: m.version, done_at: m.done_at ? null : localDay() }),
    onSuccess: refresh,
    onError: failed,
  });
  const list = [...o.milestones].sort((a, b) => a.position - b.position);
  return (
    <>
      <p className="text-meta text-p-text-muted">Вехи — то, что увидит клиент: что будет сделано и к какому дню. Внутренние можно скрыть от него.</p>
      {list.length === 0 && <EmptyNote>Вех пока нет.</EmptyNote>}
      <ol className="flex flex-col gap-2">
        {list.map((m) => (
          <li key={m.id}>
            <Card className="flex flex-col gap-1.5">
              <div className="flex items-start gap-3">
                <span className="min-w-0 flex-1">
                  <span className={m.done_at ? "block font-semibold text-p-text-muted line-through" : "block font-semibold text-p-text"}>{m.title}</span>
                  <span className="block text-meta text-p-text-muted">
                    {[m.done_at ? `готово ${day(m.done_at)}` : m.planned_at ? `к ${day(m.planned_at)}` : "без срока", m.status_note].filter(Boolean).join(" · ")}
                  </span>
                </span>
                {!m.client_visible && <Mark>клиент не видит</Mark>}
              </div>
              <div className="flex flex-wrap gap-2">
                <Button size="compact" variant="secondary" loading={done.isPending && done.variables?.id === m.id} onClick={() => done.mutate(m)}>
                  {m.done_at ? "Вернуть в работу" : "Готово"}
                </Button>
                <Button size="compact" variant="ghost" onClick={() => setEditing(m)}>Изменить</Button>
              </div>
            </Card>
          </li>
        ))}
      </ol>
      <Button variant="secondary" icon={<Plus className="h-4 w-4" aria-hidden="true" />} onClick={() => setEditing("new")}>Веха</Button>
      <MilestoneSheet key={editing === "new" ? "new" : editing?.id ?? "none"} order={o} milestone={editing} onClose={() => setEditing(null)} />
    </>
  );
}

function MilestoneSheet({ order: o, milestone, onClose }: { order: OrderOut; milestone: MilestoneOut | "new" | null; onClose: () => void }) {
  const refresh = useOrderRefresh(o.id);
  const failed = useFailure(o.id);
  const m = milestone && milestone !== "new" ? milestone : null;
  const [title, setTitle] = useState(m?.title ?? "");
  const [planned, setPlanned] = useState(m?.planned_at ?? "");
  const [note, setNote] = useState(m?.status_note ?? "");
  const [visible, setVisible] = useState(m?.client_visible ?? true);
  const save = useMutation({
    mutationFn: () => {
      const body = { title: title.trim(), planned_at: planned || null, status_note: note.trim() || null, client_visible: visible };
      return m ? Orders.updateMilestone(o.id, m.id, { ...body, version: m.version }) : Orders.addMilestone(o.id, body);
    },
    onSuccess: () => { refresh(); onClose(); },
    onError: failed,
  });
  return (
    <Sheet open={milestone != null} onClose={onClose} title={m ? "Веха" : "Новая веха"}
      footer={<Button block disabled={!title.trim()} loading={save.isPending} loadingText="Сохраняем…" onClick={() => save.mutate()}>Сохранить</Button>}>
      <div className="flex flex-col gap-3">
        <TextField label="Что будет сделано" value={title} onChange={(e) => setTitle(e.target.value)} maxLength={200} placeholder="Прототип" autoFocus />
        <DateField label="К какому дню" value={planned} onChange={setPlanned} />
        <TextField label="Что сейчас" value={note} onChange={(e) => setNote(e.target.value)} maxLength={300} placeholder="Собираем требования" hint="Одна строка — её прочитает клиент." />
        <label className="flex min-h-11 items-center gap-3 text-body text-p-text">
          <Switch on={visible} label="Клиент видит эту веху" onChange={() => setVisible(!visible)} />
          Клиент видит эту веху
        </label>
      </div>
    </Sheet>
  );
}

/* ── Documents ──────────────────────────────────────────────────────────────────────── */

function Documents({ order: o }: { order: OrderOut }) {
  const [creating, setCreating] = useState(false);
  const [deciding, setDeciding] = useState<{ doc: DocumentOut; decision: "approved" | "rejected" } | null>(null);
  const [sending, setSending] = useState<DocumentOut | null>(null);
  const [signing, setSigning] = useState<DocumentOut | null>(null);
  const list = [...o.documents].reverse();
  return (
    <>
      <p className="text-meta text-p-text-muted">Бриф, КП, договор, лицензия, акт и допсоглашение — версиями. Утверждает человек; утверждённое КП ставит сумму заказа. Договор и лицензию может утвердить «Юрист»: в договоре на месте суммы — {"{{сумма}}"}, её подставит сервер из КП. Клиенту документ уходит файлом Word — кнопкой «Отправить клиенту» или его отправляет менеджер заказа. Клиент прислал подписанный договор — «Подписанный договор».</p>
      {list.length === 0 && <EmptyNote>Документов пока нет.</EmptyNote>}
      {list.map((d) => (
        <Card key={d.id} className="flex flex-col gap-2">
          <div className="flex flex-wrap items-baseline gap-2">
            <span className="font-semibold text-p-text">{documentName(d.kind, d.revision)}</span>
            <Mark>{DOCUMENT_STATE[d.state]}</Mark>
            {d.proposed_amount_rub != null && <span className="ml-auto text-meta text-p-text-muted">{rub(d.proposed_amount_rub)}</span>}
          </div>
          <p className="text-body text-p-text">{d.title}</p>
          <p className="text-meta text-p-text-muted">
            {d.via_agent ? "подготовил агент" : "подготовил человек"} · {ago(d.created_at)}
            {d.decided_by ? ` · ${d.state === "rejected" ? "отклонил" : "утвердил"}: ${d.decided_by.name}, ${ago(d.decided_at)}` : ""}
          </p>
          {d.decision_note && <p className="text-meta text-p-text">Решение: {d.decision_note}</p>}
          <DocumentFiles doc={d} />
          <details className="space-y-2">
            <summary className="min-h-11 cursor-pointer py-2 text-body font-semibold">Текст</summary>
            <Markdown text={d.body_md} className="text-body text-p-text" />
          </details>
          {d.state === "draft" && (
            <div className="flex flex-wrap gap-2">
              <Button size="compact" onClick={() => setDeciding({ doc: d, decision: "approved" })}>Утвердить</Button>
              <Button size="compact" variant="secondary" onClick={() => setDeciding({ doc: d, decision: "rejected" })}>Отклонить</Button>
              <Button size="compact" variant="ghost" onClick={() => setSending(d)}>Утвердить и отправить</Button>
            </div>
          )}
          {d.state === "approved" && (
            <div className="flex flex-wrap gap-2">
              <Button size="compact" variant="secondary" onClick={() => setSending(d)}>Отправить клиенту</Button>
              {d.kind === "contract" && !FINISHED.includes(o.stage) && (
                <Button size="compact" variant="ghost" onClick={() => setSigning(d)}>Подписанный договор</Button>
              )}
            </div>
          )}
        </Card>
      ))}
      <Button variant="secondary" icon={<Plus className="h-4 w-4" aria-hidden="true" />} onClick={() => setCreating(true)}>Документ</Button>
      <DocumentSheet key={creating ? "open" : "shut"} order={o} open={creating} onClose={() => setCreating(false)} />
      <DecisionSheet key={deciding ? `${deciding.doc.id}-${deciding.decision}` : "none"} order={o} choice={deciding} onClose={() => setDeciding(null)} />
      <SendSheet key={sending?.id ?? "none"} order={o} doc={sending} onClose={() => setSending(null)} />
      <SignedSheet key={signing?.id ?? "none"} order={o} doc={signing} onClose={() => setSigning(null)} />
    </>
  );
}

/** Spec 061: the client sent the contract back signed — a person picks its file from the chat. */
function SignedSheet({ order: o, doc, onClose }: { order: OrderOut; doc: DocumentOut | null; onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const refresh = useOrderRefresh(o.id);
  const failed = useFailure(o.id);
  const [file, setFile] = useState("");
  const chat = useQuery({ queryKey: ["client-order", o.id, "client-files"], queryFn: () => ClientChat.get(o.id), enabled: doc != null });
  const theirs = (chat.data?.messages ?? []).filter((m) => m.direction === "in").flatMap((m) => m.files.map((f) => ({ ...f, at: m.created_at })));
  const save = useMutation({
    mutationFn: () => Orders.signed(o.id, doc!.id, { version: doc!.version, attachment_id: file }),
    onSuccess: () => {
      toast.success("Договор подписан — заказ в работе");
      refresh();
      void qc.invalidateQueries({ queryKey: ["client", o.client.id, "documents"] });
      onClose();
    },
    onError: failed,
  });
  return (
    <Sheet open={doc != null} onClose={onClose} title={doc ? `Подписанный ${documentName(doc.kind, doc.revision)}` : ""}
      footer={<Button block disabled={!file} loading={save.isPending} loadingText="Отмечаем…" onClick={() => save.mutate()}>Отметить подписанным</Button>}>
      <div className="flex flex-col gap-3">
        <p className="text-body text-p-text">Выбери файл, который клиент прислал в чат заказа. Заказ перейдёт «в работу», менеджер заказа передаст клиента.</p>
        {chat.isLoading && <SkeletonList count={2} />}
        {chat.data && theirs.length === 0 && <EmptyNote>Клиент ещё не присылал файлов в чат.</EmptyNote>}
        {theirs.length > 0 && (
          <SelectField label="Файл клиента" value={file} onChange={setFile} placeholder="Выбери файл"
            options={theirs.map((f) => ({ value: f.id, label: `${f.name} · ${ago(f.at)}` }))} />
        )}
        <p className="text-meta text-p-text-muted">Второй экземпляр подписываешь ты.</p>
      </div>
    </Sheet>
  );
}

function DocumentSheet({ order: o, open, onClose }: { order: OrderOut; open: boolean; onClose: () => void }) {
  const refresh = useOrderRefresh(o.id);
  const failed = useFailure(o.id);
  const [kind, setKind] = useState<DocumentKind>("brief");
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [sum, setSum] = useState("");
  const amount = parseRub(sum);
  const sumError = kind === "proposal" && sum.trim() && (amount == null || amount < 0) ? "Сумма в рублях" : undefined;
  const save = useMutation({
    mutationFn: () => Orders.addDocument(o.id, {
      kind, title: title.trim(), body_md: body.trim(), proposed_amount_rub: kind === "proposal" ? amount : null,
    }),
    onSuccess: () => { refresh(); onClose(); },
    onError: failed,
  });
  const ready = title.trim() && body.trim() && (kind !== "proposal" || (amount != null && amount >= 0));
  return (
    <Sheet open={open} onClose={onClose} title="Новый документ"
      footer={<Button block disabled={!ready} loading={save.isPending} loadingText="Сохраняем…" onClick={() => save.mutate()}>Сохранить черновик</Button>}>
      <div className="flex flex-col gap-3">
        <SelectField label="Что это" value={kind} onChange={(v) => setKind((v || "brief") as DocumentKind)}
          options={DOCUMENT_KINDS.map((k) => ({ value: k, label: DOCUMENT_LABEL[k] }))} />
        <TextField label="Название" value={title} onChange={(e) => setTitle(e.target.value)} maxLength={200} placeholder={`${DOCUMENT_LABEL[kind]}: ${o.title}`} />
        {kind === "proposal" && (
          <TextField label="Сумма, ₽" value={sum} onChange={(e) => setSum(e.target.value)} inputMode="numeric" error={sumError} hint="Станет суммой заказа, когда КП утвердят." />
        )}
        <TextArea label="Текст" value={body} onChange={(e) => setBody(e.target.value)} maxLength={100000} className="min-h-[200px]" />
        <p className="text-meta text-p-text-muted">Новая версия того же вида получает следующий номер: «КП v2».</p>
      </div>
    </Sheet>
  );
}

function DecisionSheet({ order: o, choice, onClose }: { order: OrderOut; choice: { doc: DocumentOut; decision: "approved" | "rejected" } | null; onClose: () => void }) {
  const refresh = useOrderRefresh(o.id);
  const failed = useFailure(o.id);
  const [note, setNote] = useState("");
  const save = useMutation({
    mutationFn: () => Orders.decideDocument(o.id, choice!.doc.id, { version: choice!.doc.version, decision: choice!.decision, note_md: note.trim() || null }),
    onSuccess: () => { refresh(); onClose(); },
    onError: failed,
  });
  const approving = choice?.decision === "approved";
  return (
    <Sheet open={choice != null} onClose={onClose} title={choice ? `${approving ? "Утвердить" : "Отклонить"} ${documentName(choice.doc.kind, choice.doc.revision)}` : ""}
      footer={<Button block disabled={!approving && !note.trim()} loading={save.isPending} loadingText="Сохраняем…" onClick={() => save.mutate()}>{approving ? "Утвердить" : "Отклонить"}</Button>}>
      <div className="flex flex-col gap-3">
        {approving && choice?.doc.kind === "proposal" && (
          <p className="text-body text-p-text">Сумма заказа станет {rub(choice.doc.proposed_amount_rub)}.</p>
        )}
        <TextArea label={approving ? "Пояснение (необязательно)" : "Почему"} value={note} onChange={(e) => setNote(e.target.value)} maxLength={2000} autoFocus />
        <p className="text-meta text-p-text-muted">Утверждённый документ уходит клиенту кнопкой «Отправить клиенту» — в чат заказа.</p>
      </div>
    </Sheet>
  );
}

/** Spec 054: a document reaches the client only by a person — an approved one, or a draft approved here. */
function SendSheet({ order: o, doc, onClose }: { order: OrderOut; doc: DocumentOut | null; onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const refresh = useOrderRefresh(o.id);
  const failed = useFailure(o.id);
  const approving = doc?.state === "draft";
  const send = useMutation({
    mutationFn: () => ClientChat.sendDocument(o.id, doc!.id, { version: doc!.version, approve: approving }),
    onSuccess: () => {
      toast.success("Документ ушёл клиенту в Telegram");
      refresh();
      void qc.invalidateQueries({ queryKey: chatKey(o.id) });
      onClose();
    },
    onError: failed,
  });
  return (
    <Sheet open={doc != null} onClose={onClose} title={doc ? `${approving ? "Утвердить и отправить" : "Отправить"} ${documentName(doc.kind, doc.revision)}` : ""}
      footer={<Button block loading={send.isPending} loadingText="Отправляем…" onClick={() => send.mutate()}>{approving ? "Утвердить и отправить" : "Отправить клиенту"}</Button>}>
      <div className="flex flex-col gap-3">
        <p className="text-body text-p-text">
          Клиент получит документ в чате заказа в Telegram{approving && doc?.kind === "proposal" ? `, а сумма заказа станет ${rub(doc.proposed_amount_rub)}` : ""}.
        </p>
        <p className="text-meta text-p-text-muted">Уходит с подписью менеджера заказа. Если чат с клиентом не подключён, сначала пригласи клиента во вкладке «Переписка».</p>
      </div>
    </Sheet>
  );
}

/* ── The units' work, the code, the history ─────────────────────────────────────────────── */

function UnitWork({ order: o }: { order: OrderOut }) {
  if (o.unit_orders.length === 0) {
    return <EmptyNote>Поручений отделов по заказу пока нет. Их даёт менеджер заказа — компетенциям, их услугами.</EmptyNote>;
  }
  return (
    <ul className="overflow-hidden rounded-card border border-p-border bg-p-surface">
      {o.unit_orders.map((u) => (
        <li key={u.id} className="flex flex-col gap-0.5 border-b border-p-border px-4 py-3 last:border-0">
          <span className="text-[15px] font-semibold text-p-text">№{u.number} · {u.title}</span>
          <span className="text-meta text-p-text-muted">{u.unit_name} · {u.assignee} · {u.state}</span>
        </li>
      ))}
    </ul>
  );
}

function CodeBlock({ order: o }: { order: OrderOut }) {
  const toast = useToast();
  const refresh = useOrderRefresh(o.id);
  const refreshCode = useOrderCodeRefresh(o.id);
  const [open, setOpen] = useState<OrderCodeReview | null>(null);
  const me = useQuery({ queryKey: ["overview"], queryFn: Overview.get, staleTime: 60_000 }).data;
  const state = useQuery({ queryKey: ["order-code", o.id, "state"], queryFn: () => OrderCode.state(o.id), retry: false });
  const reviews = useQuery({ queryKey: ["order-code", o.id, "reviews"], queryFn: () => OrderCode.reviews(o.id), enabled: state.data?.opened === true, refetchInterval: 30_000 });
  const opening = useMutation({
    mutationFn: () => OrderCode.open(o.id),
    onSuccess: () => { toast.success("Код заказа открыт"); refreshCode(); refresh(); },
    onError: (error: Error) => toast.error(error.message),
  });
  const download = useMutation({
    mutationFn: () => OrderCode.bundle(o.id),
    onSuccess: (blob) => {
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `order-${o.number}.bundle`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 10_000);
    },
    onError: (error: Error) => toast.error(error.message),
  });
  if (state.isLoading) return <SkeletonList count={1} />;
  if (!state.data) return <EmptyNote action={<Button size="compact" variant="secondary" onClick={() => void state.refetch()}>Повторить</Button>}>Хранилище кода не ответило.</EmptyNote>;
  if (!state.data.opened) {
    const finished = FINISHED.includes(o.stage);
    return (
      <EmptyNote action={!finished && <Button size="compact" loading={opening.isPending} loadingText="Открываем…" onClick={() => opening.mutate()}>Открыть код сейчас</Button>}>
        {finished ? "Заказ закрыт или потерян — код не открываем." : "Код заказа откроется сам на «Договоре». Для прототипа на этапе КП его можно открыть раньше."}
      </EmptyNote>
    );
  }
  return (
    <>
      <p className="text-meta text-p-text-muted">
        Код пишет исполнитель поручения по заказу в своей ветке; ревью приходит тебе в «Решить». Основная ветка: {state.data.head?.slice(0, 8)}.
      </p>
      <Button variant="secondary" icon={<Download className="h-4 w-4" aria-hidden="true" />} loading={download.isPending} loadingText="Готовим…" onClick={() => download.mutate()}>
        Скачать исходники для клиента
      </Button>
      <p className="text-meta text-p-text-muted">Файл Git bundle с основной веткой: клиент получит весь код и его историю. Передаёшь его ты.</p>
      {reviews.isLoading && <SkeletonList count={1} />}
      {reviews.isError && <EmptyNote>Ревью не загрузились.</EmptyNote>}
      {reviews.data && reviews.data.reviews.length === 0 && <p className="text-meta text-p-text-muted">Ревью кода пока не было.</p>}
      {reviews.data?.reviews.map((r) => (
        <Button key={r.id} variant="secondary" block className="h-auto min-h-12 min-w-0 justify-start whitespace-normal break-words py-3 text-left"
          onClick={() => setOpen(open?.id === r.id ? null : r)}>
          {REVIEW_LABEL[r.status]} · {r.head.slice(0, 8)} · {r.summary.slice(0, 90)}
        </Button>
      ))}
      {open && <OrderCodeReviewPanel key={open.id} orderId={o.id} item={open} me={me?.me.id ?? undefined} />}
    </>
  );
}

function History({ order: o }: { order: OrderOut }) {
  const list = [...o.events].reverse();
  if (list.length === 0) return <EmptyNote>Истории пока нет.</EmptyNote>;
  return (
    <ol className="flex flex-col gap-2">
      {list.map((e) => (
        <li key={e.id} className="flex flex-col gap-0.5 border-l-2 border-p-border pl-3">
          <span className="text-body text-p-text">{eventLine(e)}</span>
          <span className="text-meta text-p-text-muted">{whoDid(e.actor_name, e.via_agent)} · {ago(e.created_at)}</span>
          {e.note_md && <span className="whitespace-pre-wrap text-meta text-p-text">{e.note_md}</span>}
        </li>
      ))}
    </ol>
  );
}
